import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, UsersIcon, DownloadIcon, StethoscopeIcon, LockIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Specialty } from '../../types';
import { exportSpecialtiesToExcel } from '../../utils/excelExport';
import { exportSpecialtiesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Specialties() {
  const {
    specialties,
    addSpecialty,
    updateSpecialty,
    deleteSpecialty,
    getDoctorsBySpecialty
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('specialties');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-specialties-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | null>(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateSpecialty(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateSpecialty(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateSpecialty(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const openActionConfirm = (specialty: Specialty, type: 'archive' | 'deactivate') => {
    setSelectedSpecialty(specialty);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedSpecialty || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedSpecialty.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedSpecialty.id);
    }
  };
  const filteredSpecialties = specialties.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(s.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedSpecialties,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredSpecialties);
  // Counts for tabs
  const activeCount = specialties.filter(s => getItemStatus(s.id) === 'active').length;
  const archivedCount = specialties.filter(s => getItemStatus(s.id) === 'archived').length;
  const deactivatedCount = specialties.filter(s => getItemStatus(s.id) === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('specialties')) return;
    setSelectedSpecialty(null);
    setFormData({
      name: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (specialty: Specialty) => {
    if (!canEdit('specialties')) return;
    setSelectedSpecialty(specialty);
    setFormData({
      name: specialty.name,
      description: specialty.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (specialty: Specialty) => {
    if (!canDelete('specialties')) return;
    setSelectedSpecialty(specialty);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedSpecialty) {
      updateSpecialty(selectedSpecialty.id, formData);
    } else {
      addSpecialty(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('specialties')) return;
    if (selectedSpecialty) {
      deleteSpecialty(selectedSpecialty.id);
      setIsDeleteConfirmOpen(false);
      setSelectedSpecialty(null);
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportSpecialtiesToExcel(specialties);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportSpecialtiesToPdf(specialties);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-4072">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500 p-6 text-white shadow-xl" data-id="element-4073">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-4074" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-4075">
          <div data-id="element-4076">
            <div className="flex items-center gap-3 mb-2" data-id="element-4077">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-4078">
                <StethoscopeIcon className="w-6 h-6" data-id="element-4079" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-4080">Especialidades</h1>
            </div>
            <p className="text-rose-100 max-w-xl" data-id="element-4081">
              Gestión de especialidades médicas
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-4082">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-4083">
            {canExportPdf && <Button variant="outline" leftIcon={<StethoscopeIcon className="w-4 h-4" data-id="element-4085" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4084">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4087" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4086">
                Exportar Excel
              </Button>}
            {canCreate('specialties') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4089" />} onClick={openNewForm} className="bg-white text-rose-700 hover:bg-rose-50 shadow-lg shadow-rose-900/20" data-id="element-4088">
                Nueva Especialidad
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-4090">
        <div className="w-full md:w-96 relative" data-id="element-4091">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4092" />
          <Input placeholder="Buscar especialidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-4093" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-4094">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4095">
              <tab.icon className="w-4 h-4" data-id="element-4096" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-4097">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-4098">
          <LockIcon className="w-4 h-4" data-id="element-4099" />
          <span data-id="element-4100">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            especialidades.
          </span>
        </div>}

      {/* Specialties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4101">
        {paginatedSpecialties.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-4102">
            <div className="flex flex-col items-center justify-center" data-id="element-4103">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-4104">
                <StethoscopeIcon className="w-8 h-8 text-gray-400" data-id="element-4105" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-4106">
                No se encontraron especialidades
              </h3>
              <p className="text-sm" data-id="element-4107">
                {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando una nueva especialidad' : 'No hay especialidades en esta vista'}
              </p>
            </div>
          </div> : paginatedSpecialties.map(specialty => {
        const doctorCount = getDoctorsBySpecialty(specialty.id).length;
        return <Card key={specialty.id} data-id="element-4108">
                <div className="flex items-start justify-between mb-3" data-id="element-4109">
                  <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4110">
                    {specialty.name}
                  </h3>

                  {!readOnly && viewMode === 'active' && <div className="flex gap-1" data-id="element-4111">
                      {canEdit('specialties') && <button onClick={() => openEditForm(specialty)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar especialidad" data-id="element-4112">
                          <EditIcon className="w-4 h-4" data-id="element-4113" />
                        </button>}

                      {canDelete('specialties') && <>
                          <button onClick={() => openActionConfirm(specialty, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-4114">
                            <ArchiveIcon className="w-4 h-4" data-id="element-4115" />
                          </button>
                          <button onClick={() => openActionConfirm(specialty, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-4116">
                            <PowerOffIcon className="w-4 h-4" data-id="element-4117" />
                          </button>
                        </>}
                    </div>}

                  {viewMode !== 'active' && <button onClick={() => restoreItem(specialty.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-4118">
                      <RotateCcwIcon className="w-4 h-4" data-id="element-4119" />
                    </button>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4120">
                  {specialty.description}
                </p>

                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-4121">
                  <UsersIcon className="w-4 h-4" data-id="element-4122" />
                  <span data-id="element-4123">
                    {doctorCount} {doctorCount === 1 ? 'doctor' : 'doctores'}
                  </span>
                </div>
              </Card>;
      })}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-4124" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedSpecialty ? 'Editar Especialidad' : 'Nueva Especialidad'} icon={<StethoscopeIcon className="w-5 h-5" data-id="element-4126" />} accentColor="rose" data-id="element-4125">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4127">
          {/* Specialty Info */}
          <div data-id="element-4128">
            <div className="flex items-center gap-2 mb-4" data-id="element-4129">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-4130"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4131">
                Información de la Especialidad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4132">
              <Input label="Nombre de la Especialidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Cardiología, Pediatría..." data-id="element-4133" />
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción de la especialidad..." data-id="element-4134" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-4135">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-4136">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4137">
              {selectedSpecialty ? 'Guardar Cambios' : 'Crear Especialidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Especialidad' : 'Desactivar Especialidad'} size="sm" data-id="element-4138">
        <div className="space-y-4" data-id="element-4139">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-4140">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedSpecialty?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedSpecialty?.name}"? No aparecerá en las listas activas.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-4141">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-4142">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleAction} data-id="element-4143">
              {actionType === 'archive' ? 'Archivar' : 'Desactivar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Especialidades" isExporting={isExporting} data-id="element-4144" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Especialidades a PDF" isExporting={isPdfExporting} data-id="element-4145" />
    </div>;
}